import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, Chip } from '@mui/material';
import { ArrowBack, StorefrontOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { GRADIENTS } from '../../theme/tokens';

const TOP_BAR_HEIGHT = 52;

/**
 * Shared chrome for all marketing demos:
 * - Fixed top bar with Back button, DEMO badge, and optional title.
 * - Floating "List your library" CTA anchored to bottom-right.
 * - Adds top padding so page content is not hidden beneath the bar.
 */
function DemoChrome({ title, children }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const barBg = isDark
    ? 'rgba(11,18,32,0.95)'
    : 'rgba(255,255,255,0.96)';
  const barBorder = isDark
    ? '1px solid rgba(255,255,255,0.08)'
    : '1px solid rgba(15,23,42,0.08)';

  return (
    <>
      {/* ── Fixed top bar ─────────────────────────────────────────── */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: TOP_BAR_HEIGHT,
          zIndex: 1400,
          bgcolor: barBg,
          borderBottom: barBorder,
          backdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
        }}
      >
        {/* Back button */}
        <Button
          size="small"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/join-us-as-library')}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            fontWeight: 600,
            minWidth: 0,
            px: 1,
            '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
          }}
          aria-label="Back to join us page"
        >
          Back
        </Button>

        {/* DEMO badge */}
        <Chip
          label="DEMO"
          size="small"
          sx={{
            background: GRADIENTS.brand,
            color: '#fff',
            fontWeight: 800,
            fontSize: '0.65rem',
            letterSpacing: '0.08em',
            height: 22,
            borderRadius: '999px',
          }}
        />

        {/* Title */}
        {title && (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              display: { xs: 'none', sm: 'block' },
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
        )}

        {/* Caption */}
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            ml: 'auto',
            display: { xs: 'none', md: 'block' },
            whiteSpace: 'nowrap',
          }}
        >
          Interactive demo · not live data
        </Typography>
      </Box>

      {/* ── Content area ──────────────────────────────────────────── */}
      <Box sx={{ pt: `${TOP_BAR_HEIGHT}px` }}>
        {children}
      </Box>

      {/* ── Floating CTA ──────────────────────────────────────────── */}
      <Button
        variant="contained"
        size="small"
        startIcon={<StorefrontOutlined />}
        onClick={() => {
          window.location.assign('/join-us-as-library#signup');
        }}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 20,
          zIndex: 1400,
          background: GRADIENTS.brand,
          color: '#fff',
          fontWeight: 700,
          textTransform: 'none',
          borderRadius: '999px',
          px: 2.5,
          py: 1,
          fontSize: '0.8rem',
          boxShadow: '0 8px 24px rgba(242,101,34,0.35)',
          '&:hover': {
            background: GRADIENTS.brandDeep,
            boxShadow: '0 12px 32px rgba(242,101,34,0.45)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        List your library
      </Button>
    </>
  );
}

DemoChrome.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
};

DemoChrome.defaultProps = {
  title: '',
};

export default DemoChrome;
